import { JSX, useContext, useEffect } from 'react';
import { useLocation, useNavigationType } from 'react-router-dom';
import { createRoutesFromChildren, matchRoutes } from 'react-router';
import * as Sentry from '@sentry/react';
import type { BrowserOptions } from '@sentry/browser';
import { AppContext } from './App';

export const sentryOptions: BrowserOptions = {
  enabled: 'true'.localeCompare(import.meta.env.VITE_SENTRY_ENABLED) === 0,
  dsn: import.meta.env.VITE_SENTRY_DSN,
  environment: import.meta.env.VITE_ENV,
  release: `ap-crmcards-backend@${import.meta.env.VITE_VERSION}`,
  /**
   * It controls for which URLs distributed tracing should be enabled
   * TODO: add import.meta.env.VITE_API_URL when NestJS will be ready
   *  <!> if the env variable is passed but empty, the site will crash because the Sentry headers will be passed to
   *  the IDP and rejected by the CORS policy
   */
  tracePropagationTargets: ['localhost', import.meta.env.VITE_AZURE_FUNCTION_URL],
  integrations: [
    Sentry.reactRouterV6BrowserTracingIntegration({
      useEffect,
      useLocation,
      useNavigationType,
      createRoutesFromChildren,
      matchRoutes,
    }),
    /* Sentry.replayIntegration({
      maskAllText: true,
      blockAllMedia: true,
    }), */
  ],
  normalizeDepth: 5,
  tracesSampleRate: Number(import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE),
  replaysSessionSampleRate: Number(import.meta.env.VITE_SENTRY_REPLAYS_SESSION_SAMPLE_RATE),
  replaysOnErrorSampleRate: Number(import.meta.env.VITE_SENTRY_REPLAYS_ON_ERROR_SAMPLE_RATE),
  beforeBreadcrumb(breadcrumb) {
    if (breadcrumb.category === 'console' && breadcrumb.message?.startsWith('%c')) {
      // Exclude AppConsole console output
      return null;
    }
    return breadcrumb;
  },
};

interface Props {
  children: JSX.Element;
}

function AppSentryProvider(props: Readonly<Props>) {
  const { user } = useContext(AppContext);

  useEffect(() => {
    if (user) {
      Sentry.setUser({
        id: user.id,
        username: user.id,
        segment: user.roles ? user.roles.map((role) => role.toString()).join(', ') : '',
      });
    }
  }, [user?.id, user?.roles]);

  return <Sentry.ErrorBoundary>{props.children}</Sentry.ErrorBoundary>;
}

export default Sentry.withProfiler(AppSentryProvider);
